<script>
import { computed } from 'vue';
import { mapGetters, useStore } from 'vuex';
import { useKaptionFeatureFlags } from 'dashboard/composables/useKaptionFeatureFlags';
import { useFeaturePermissions } from 'dashboard/composables/useFeaturePermissions';
import { useKaptionAIFeaturesPreferences } from 'dashboard/composables/useKaptionAIFeaturesPreferences';
import KaptionMentionBox from '../mentions/KaptionMentionBox.vue';
import kaptionAiSuggestionsAPI from 'dashboard/api/kaptionAiSuggestions';

export default {
  components: { KaptionMentionBox },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  emits: ['replace'],
  setup() {
    // NOTICE: KAPTION-INTEGRATION - Use the composable hook for feature flags
    const { isKaptionFlagEnabled } = useKaptionFeatureFlags();
    // NOTICE: KAPTION-INTEGRATION - Client-managed feature permission layer
    const { canUseFeature } = useFeaturePermissions();
    const store = useStore();

    // Create a computed ref for conversationId to use with preferences composable
    const conversationIdRef = computed(() => {
      const currentChat = store.getters.getSelectedChat;
      return currentChat?.id;
    });

    // NOTICE: KAPTION-INTEGRATION - Two-gate system: feature flag + user preference
    const { suggestionsEnabled } =
      useKaptionAIFeaturesPreferences(conversationIdRef);

    return {
      isKaptionFlagEnabled,
      canUseFeature,
      suggestionsEnabled,
    };
  },
  data() {
    return {
      aiSuggestions: [],
      isLoadingAiSuggestions: false,
      aiSuggestionsError: null,
    };
  },
  computed: {
    ...mapGetters({
      cannedMessages: 'getCannedResponses',
      currentChat: 'getSelectedChat',
      currentAccountId: 'getCurrentAccountId',
    }),
    // NOTICE: KAPTION-INTEGRATION - Get values from store like ReplyBox does
    conversationId() {
      return this.currentChat?.id;
    },
    inboxId() {
      return this.currentChat?.inbox_id;
    },
    accountId() {
      return this.currentAccountId;
    },
    cannedItems() {
      const cannedItems = this.cannedMessages.map(cannedMessage => ({
        label: cannedMessage.short_code,
        key: `canned-${cannedMessage.short_code}`,
        description: cannedMessage.content,
        type: 'canned',
        attachments: cannedMessage.attachments || [],
      }));

      // Canned responses first, then AI suggestions
      const allItems = [...cannedItems];

      return allItems;
    },
  },
  watch: {
    searchKey() {
      this.fetchCannedResponses();
    },
    // NOTICE: KAPTION-INTEGRATION - Watch user preference toggle
    suggestionsEnabled(newValue) {
      if (!newValue) {
        // Clear suggestions when disabled
        this.aiSuggestions = [];
        this.aiSuggestionsError = null;
      } else {
        // Fetch suggestions when enabled
        this.fetchAiSuggestions();
      }
    },
    conversationId() {
      // Refetch when conversation changes
      if (this.conversationId && this.inboxId && this.accountId) {
        this.fetchAiSuggestions();
      }
    },
  },
  mounted() {
    this.fetchCannedResponses();
    // Delay AI suggestions fetch to ensure props are ready
    this.$nextTick(() => {
      if (this.conversationId && this.inboxId && this.accountId) {
        this.fetchAiSuggestions();
      }
    });
  },
  methods: {
    fetchCannedResponses() {
      this.$store.dispatch('getCannedResponse', { searchKey: this.searchKey });
    },
    async fetchAiSuggestions() {
      // Only fetch if we have the required props
      if (!this.conversationId || !this.inboxId || !this.accountId) {
        return;
      }

      // NOTICE: KAPTION-INTEGRATION - Two-gate system: feature permission + user preference
      const flagEnabled = this.canUseFeature('ai_reply_suggestions');
      if (!flagEnabled || !this.suggestionsEnabled) {
        return;
      }

      if (this.isLoadingAiSuggestions) {
        return;
      }

      this.isLoadingAiSuggestions = true;
      this.aiSuggestionsError = null;

      try {
        const response = await kaptionAiSuggestionsAPI.fetchSuggestions({
          conversationId: this.conversationId,
          inboxId: this.inboxId,
        });

        if (response.data && response.data.suggestions) {
          this.aiSuggestions = response.data.suggestions;
        } else {
          this.aiSuggestions = [];
        }
      } catch (err) {
        this.aiSuggestionsError = err.response?.data?.error || err.message;
        this.aiSuggestions = [];
      } finally {
        this.isLoadingAiSuggestions = false;
      }
    },
    handleMentionClick(item = {}) {
      this.$emit('replace', {
        text: item.description,
        attachments: item.attachments || [],
      });
    },
    handleAiSuggestionClick(item = {}) {
      // AI suggestions emit the full item, extract the text
      const text = item.text || item;
      this.$emit('replace', { text, attachments: [] });
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div class="canned-response-container">
    <!-- Canned Responses and AI Suggestions combined -->
    <KaptionMentionBox
      v-if="
        cannedItems.length || aiSuggestions.length || isLoadingAiSuggestions
      "
      :items="cannedItems"
      :ai-suggestions="aiSuggestions"
      :is-loading-ai="isLoadingAiSuggestions"
      @mention-select="handleMentionClick"
      @ai-select="handleAiSuggestionClick"
    />
  </div>
</template>

<style scoped lang="scss">
.canned-response-container {
  position: relative;
}

.ai-suggestions-list {
  position: absolute;
  left: 0;
  bottom: 100%;
  width: 100%;
  z-index: 20;
  margin-bottom: 0.5rem;
}
</style>
